import { CognitoUserPool, CognitoUserSession } from "amazon-cognito-identity-js";

const userPool = new CognitoUserPool({
  UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID as string,
  ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID as string,
});

export interface AuthUser {
  sub: string;
  email: string;
  first: string;
  last: string;
  isAdmin: boolean;
}

function userFromSession(session: CognitoUserSession): AuthUser {
  const payload = session.getIdToken().decodePayload() as Record<string, unknown>;
  const groupsRaw = payload["cognito:groups"];
  const groups: string[] = Array.isArray(groupsRaw)
    ? (groupsRaw as string[])
    : typeof groupsRaw === "string"
      ? [groupsRaw]
      : [];
  return {
    sub: String(payload.sub ?? ""),
    email: String(payload.email ?? ""),
    first: String(payload.given_name ?? ""),
    last: String(payload.family_name ?? ""),
    isAdmin: groups.includes("admin"),
  };
}

export async function currentUser(): Promise<AuthUser | null> {
  const cog = userPool.getCurrentUser();
  if (!cog) return null;
  return new Promise((resolve) => {
    cog.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve(userFromSession(session));
    });
  });
}

export async function idToken(): Promise<string | null> {
  const cog = userPool.getCurrentUser();
  if (!cog) return null;
  return new Promise((resolve) => {
    cog.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        resolve(null);
        return;
      }
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

export function signOut(): void {
  userPool.getCurrentUser()?.signOut();
}

// Where to send the user after sign-in completes. Stashed before the redirect
// (we leave the SPA entirely for the Hosted UI) and read back in AuthCallback.
const RETURN_TO_KEY = "r4b_return_to";

/**
 * Start sign-in via the Cognito Hosted UI.
 *
 * The email/password form AND the Google button both render on Cognito's own
 * domain (…amazoncognito.com), so NO credential input is ever served from
 * ride4baker.org. This removes the "password form on a young charity domain"
 * signal that repeatedly tripped Google Safe Browsing's phishing classifier.
 *
 * Cognito bounces back to /auth/callback with ?code=…, which AuthCallback
 * exchanges for tokens (the same code flow Google federation already used).
 * Omitting identity_provider makes Cognito show its managed login page (native
 * email/password + Google + sign-up + forgot-password) instead of jumping
 * straight to Google.
 */
export function startHostedSignIn(returnTo = "/"): void {
  const domain = import.meta.env.VITE_COGNITO_DOMAIN as string;
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string;
  const redirect = `${import.meta.env.VITE_PUBLIC_BASE_URL}/auth/callback`;
  try {
    sessionStorage.setItem(RETURN_TO_KEY, returnTo);
  } catch {
    // sessionStorage unavailable (e.g. private mode) — fall back to home.
  }
  const params = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: redirect,
    scope: "openid email profile",
  });
  window.location.href = `https://${domain}/oauth2/authorize?${params}`;
}

/** Read and clear the post-sign-in return path stashed by startHostedSignIn. */
export function takeReturnTo(): string {
  try {
    const v = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
    return v || "/";
  } catch {
    return "/";
  }
}
